// Cobro del carrito: qué falta para «Cobrar», el request de la venta y su llave de idempotencia.
// Las formas de pago son las del Cobro del wizard (`PaymentListForm`), sin Wallet Fiado.
import type { CreateAccessorySaleRequest } from '@/api/services/retail-wizard/accessorySales.client';
import {
    paymentsBlocker,
    toSalePayments,
    type PaymentDraft,
} from '@/domains/sales/wizard/salePayments';
import { isService } from '@/domains/sales/wizard/catalogAvailability';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import type { CartLine, CartSummary } from './accessoriesCart';

/** Serie de cada pieza por SKU, en el orden en que se capturó. */
export type CartIdentifiers = Readonly<Record<string, readonly string[]>>;

/** Pieza suelta rastreada por IMEI o serie: el silo exige una serie por unidad. Un servicio no lleva. */
export function isTrackedPiece(
    item: Pick<CatalogItem, 'trackingMode' | 'components' | 'itemType'>,
): boolean {
    if (isService(item)) return false;
    return (
        !item.components?.length && (item.trackingMode === 'SERIAL' || item.trackingMode === 'IMEI')
    );
}

/** Las series capturadas de la línea, sin vacíos; `undefined` si la pieza no se rastrea. */
export function lineIdentifiers(
    line: CartLine,
    identifiers: CartIdentifiers,
): string[] | undefined {
    if (!isTrackedPiece(line.item)) return undefined;
    return (identifiers[line.item.sku] ?? [])
        .slice(0, line.qty)
        .map((value) => value.trim())
        .filter((value) => value !== '');
}

/** Toda pieza rastreada tiene una serie por unidad y ninguna se repite. */
export function identifiersComplete(
    lines: readonly CartLine[],
    identifiers: CartIdentifiers,
): boolean {
    return lines.every((line) => {
        const captured = lineIdentifiers(line, identifiers);
        if (captured === undefined) return true;
        return captured.length === line.qty && new Set(captured).size === captured.length;
    });
}

export interface CheckoutGate {
    unitsCount: number;
    identifiersComplete: boolean;
    totalCents: number;
    payments: readonly PaymentDraft[];
}

/** Lo primero que falta para poder cobrar; `null` si ya se puede. */
export function checkoutBlocker(gate: CheckoutGate): string | null {
    if (gate.unitsCount === 0) return 'Agrega artículos al carrito.';
    if (!gate.identifiersComplete) return 'Captura la serie de cada pieza.';
    return paymentsBlocker(gate.payments, gate.totalCents);
}

/** Request de la venta: líneas sin precio, total cobrado y sus formas de pago. */
export function buildAccessorySaleRequest(
    summary: Pick<CartSummary, 'lines' | 'subtotalCents'>,
    payments: readonly PaymentDraft[],
    identifiers: CartIdentifiers = {},
): CreateAccessorySaleRequest {
    return {
        lines: summary.lines.map((line) => {
            const pieces = lineIdentifiers(line, identifiers);
            return {
                sku: line.item.sku,
                qty: line.qty,
                ...(pieces ? { identifiers: pieces } : {}),
            };
        }),
        amountCents: summary.subtotalCents,
        payments: toSalePayments(payments, summary.subtotalCents),
    };
}

export interface IdempotencySlot {
    fingerprint: string;
    key: string;
}

/** Misma venta, misma llave: el reintento no cobra dos veces. Otra venta estrena llave. */
export function idempotencySlotFor(
    previous: IdempotencySlot | null,
    request: CreateAccessorySaleRequest,
    newKey: () => string,
): IdempotencySlot {
    const fingerprint = JSON.stringify(request);
    if (previous?.fingerprint === fingerprint) return previous;
    return { fingerprint, key: newKey() };
}
